import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, UntypedFormControl, ValidationErrors, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
  CWQuestionCategory,
  CWQuestionDifficultyEnum,
  CWQuestionType,
  CWQuestionVisibility,
  ICWQuestion,
} from '../../../../../../Common/model/cwquestion';
import { FilterCWQuestion } from '../../../../../../Common/util/cwquestion.util';
import { CWQuestionService } from '../../../services/cwquestion/cwquestion.service';
import { UserService } from '../../../services/user/user.service';
import { FlexibleButtonInMenu, FlexibleButtonType } from '../../common/flexable-button-menu/model/flexible.button.in.menu';
import { MessageBoxType } from '../../dialog/message-box-content/message-box-content.component';
import { DialogService } from '../../dialog/service/dialog.service';
import { CanChildDeactivate } from '../../util/can.deactivate.component';
import { CrosswordConstants } from '../../util/crossword.constants';
import { EditQuestionTiles } from './edit-question-tiles/edit-question-tiles.component';

export interface EditQuestionData {
  cwQuestion: ICWQuestion;
  crosswordBuildId?: string;
  canOpenInFullView?: boolean;
  filter?: FilterCWQuestion;
  isInFullView?: boolean;
}

export interface QuestionFormData {
  question: FormControl<string>;
  answer: FormControl<string>;
  category: FormControl<CWQuestionCategory>;
  difficulty: FormControl<CWQuestionDifficultyEnum>;
  visibility: FormControl<CWQuestionVisibility>;
  tags: FormControl<string[]>;
  tagInput: FormControl<string>;
  type: FormControl<CWQuestionType>;
  imageUrl: FormControl<string>;
  imageId: FormControl<string>;
}

@Component({
  selector: 'app-edit-question',
  templateUrl: './edit-question.component.html',
  styleUrls: ['./edit-question.component.scss'],
})
export class EditQuestionComponent implements OnInit {
  Difficulties = CrosswordConstants.Difficulties;
  CWQuestionVisiblities = CrosswordConstants.CWQuestionVisiblities;
  CWQuestionCategories = CrosswordConstants.CWQuestionCategories;
  cwQuestionForm: FormGroup<QuestionFormData>;
  editQuestionTiles: EditQuestionTiles;
  editQuestionButtons: FlexibleButtonInMenu[];
  @Input() editQuestionData: EditQuestionData;
  @Input() canDeactivate: CanChildDeactivate;
  @Output() cancel = new EventEmitter<void>();
  @Output() save = new EventEmitter<ICWQuestion>();
  private destroyRef = inject(DestroyRef);
  constructor(
    private translateService: TranslateService,
    private cwQuestionService: CWQuestionService,
    private dialogService: DialogService,
    private userService: UserService,
  ) {}
  ngOnInit(): void {
    this.editQuestionTiles = { question: this.editQuestionData.cwQuestion.base, filter: this.editQuestionData.filter };
    this.createForm();
    this.updateEditQuestionButtons();
    this.cwQuestionForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.updateEditQuestionButtons();
    });
    if (this.canDeactivate) {
      this.canDeactivate.hasUnsavedChanges = () => {
        return this.cwQuestionForm.dirty || this.cwQuestionForm.touched;
      };
    }
  }
  onCancel() {
    this.cancel.emit();
  }

  private createForm() {
    const cwQuestion = this.editQuestionData.cwQuestion;
    this.cwQuestionForm = new FormGroup<QuestionFormData>({
      question: new FormControl(cwQuestion.base.question, [Validators.required, Validators.maxLength(50)]),
      answer: new FormControl(cwQuestion.base.answer, [
        Validators.required,
        Validators.pattern(this.translateService.instant('validators.supported-chars')),
        (control: UntypedFormControl) => this.validateAnswerInput(control),
      ]),
      category: new FormControl(cwQuestion.category, [Validators.required]),
      difficulty: new FormControl(cwQuestion.difficulty, [Validators.required]),
      visibility: new FormControl(cwQuestion.visibility, [Validators.required]),
      tags: new FormControl(cwQuestion.tags),
      tagInput: new FormControl<string>(''),
      type: new FormControl<CWQuestionType>(cwQuestion.base.type),
      imageUrl: new FormControl<string>(cwQuestion.imageUrl),
      imageId: new FormControl<string>(cwQuestion.base.imageId),
    });
    this.cwQuestionForm.controls.imageUrl.valueChanges.subscribe(() => {
      this.cwQuestionForm.controls.imageId.setValue(null);
    });
  }
  async onSaveQuestion() {
    const cwQuestion = this.editQuestionData.cwQuestion;
    const value = this.cwQuestionForm.value;

    const cwQuestionTmp: ICWQuestion = {
      ...cwQuestion,
      base: { ...cwQuestion.base, question: value.question, answer: value.answer, type: value.type, imageId: value.imageId },
      difficulty: value.difficulty,
      visibility: value.visibility,
      category: value.category,
      tags: this.getTagsToSave(),
      imageUrl: value.imageUrl,
    };
    if (await this.cwQuestionService.doesQuestionAndAnswerExist(cwQuestionTmp.base)) {
      this.dialogService.showMessageBox(
        '',
        this.translateService.instant('errors.question-and-answer-already-exists'),
        MessageBoxType.Error,
      );
      return;
    }
    const savedQuestion = await this.dialogService.wrapInProgress(
      () => this.saveQuestion(cwQuestionTmp),
      this.translateService.instant('messages.saving-question'),
    );
    this.save.emit(savedQuestion);
  }

  private validateAnswerInput(control: UntypedFormControl): ValidationErrors | null {
    const answer: string = control.value;
    if (!this.editQuestionData.filter) {
      return null;
    }
    const filter = this.editQuestionData.filter;
    if (!filter.lengths.find((length) => answer.length == length)) {
      return { notOneOfFilterLengths: true };
    }
    return null;
  }
  private async saveQuestion(cwQuestionTmp: ICWQuestion) {
    if (cwQuestionTmp._id) {
      return await this.cwQuestionService.updateCWQuestion(cwQuestionTmp);
    } else {
      const createCWQuestion: ICWQuestion = { ...cwQuestionTmp, author: this.userService.getLoggedIn() };
      return await this.cwQuestionService.createCWQuestion(createCWQuestion);
    }
  }

  private updateEditQuestionButtons() {
    const hasImage = this.cwQuestionForm.value.imageUrl?.length > 0;
    this.editQuestionButtons = [
      {
        id: 'edit-question-save',
        text: this.translateService.instant('buttons.save'),
        icon: 'save',
        standardAction: { event: () => this.onSaveQuestion() },
        disabled: !this.cwQuestionForm.valid,
        type: FlexibleButtonType.Standard,
      },
      {
        id: 'edit-question-cancel',
        text: this.translateService.instant('buttons.cancel'),
        icon: 'cancel',
        visible: this.editQuestionData.isInFullView != true,
        standardAction: { event: () => this.onCancel() },
        type: FlexibleButtonType.Standard,
      },
      {
        id: 'edit-question-remove-image',
        text: this.translateService.instant('buttons.remove-image'),
        icon: 'delete',
        visible: hasImage,
        standardAction: { event: () => this.onRemoveImage() },
        type: FlexibleButtonType.Standard,
      },
    ];
  }

  private getTagsToSave(): string[] {
    const value = this.cwQuestionForm.value;
    return value.tagInput ? [...value.tags, value.tagInput] : value.tags;
  }

  private onRemoveImage() {
    this.cwQuestionForm.controls.imageUrl.setValue('');
    this.cwQuestionForm.controls.imageId.setValue(null);
    this.cwQuestionForm.controls.imageUrl.markAsDirty();
    this.cwQuestionForm.controls.imageId.markAsDirty();
  }
}
